import { useEffect, useRef, useState, } from 'react';

// 読み取り枠(中央)の割合。UI のガイド枠と揃える
const ROI_RATIO = 0.7;

const constraints = {
  audio: false,
  video: {
    facingMode: { ideal: 'environment' },
    width: { ideal: 1920 },
    height: { ideal: 1080 },
    frameRate: { ideal: 30 },
  },
};

export const supportsBarcodeDetector = async () => {
  if(typeof window === 'undefined' || window.BarcodeDetector == null) return false;
  try {
    const formats = await window.BarcodeDetector.getSupportedFormats();
    return formats.includes('qr_code');
  } catch(e) {
    return false;
  }
};

// video の中央 ROI_RATIO をキャンバスに切り出す
const drawRoi = (video, canvas) => {
  const { videoWidth: w, videoHeight: h } = video;
  if(w === 0 || h === 0) return false;
  const size = Math.floor(Math.min(w, h) * ROI_RATIO);
  const sx = Math.floor((w - size) / 2);
  const sy = Math.floor((h - size) / 2);
  if(canvas.width !== size) {
    canvas.width = size;
    canvas.height = size;
  }
  canvas.getContext('2d', { willReadFrequently: true }).drawImage(video, sx, sy, size, size, 0, 0, size, size);
  return true;
};

// 端末が対応していればオートフォーカスを継続モードに固定する
const applyFocusMode = async (track) => {
  try {
    const capabilities = track.getCapabilities?.();
    if(capabilities?.focusMode?.includes('continuous')) {
      await track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] });
    }
  } catch(e) {
    // 非対応端末では無視してよい
  }
};

export default function useQrScanner ({ paused, onDecode, onError }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const trackRef = useRef(null);
  const pausedRef = useRef(paused);
  const onDecodeRef = useRef(onDecode);
  const [torchState, setTorchState] = useState({ isAvailable: false, isOn: false });

  pausedRef.current = paused;
  onDecodeRef.current = onDecode;

  useEffect(() => {
    let stopped = false;
    let rafId = null;

    const start = async () => {
      const canvas = document.createElement('canvas');

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if(stopped) {
        stream.getTracks().forEach(_ => _.stop());
        return;
      }
      streamRef.current = stream;
      const [track] = stream.getVideoTracks();
      trackRef.current = track;
      await applyFocusMode(track);
      setTorchState({ isAvailable: track.getCapabilities?.().torch === true, isOn: false });

      const video = videoRef.current;
      video.srcObject = stream;
      video.setAttribute('playsinline', 'true');
      await video.play();

      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      const tick = async () => {
        if(stopped) return;
        if(!pausedRef.current && video.readyState >= 2 && drawRoi(video, canvas)) {
          try {
            const [barcode] = await detector.detect(canvas);
            if(barcode != null && !stopped) onDecodeRef.current(barcode.rawValue);
          } catch(e) {
            // 一過性の検出エラーは無視して次フレームへ
          }
        }
        if(!stopped) rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    };

    start().catch((e) => {
      if(!stopped) onError(e);
    });

    return () => {
      stopped = true;
      rafId != null && cancelAnimationFrame(rafId);
      streamRef.current?.getTracks().forEach(_ => _.stop());
      streamRef.current = null;
      trackRef.current = null;
    };
  }, []);

  const setTorch = async (isOn) => {
    const track = trackRef.current;
    if(track == null) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: isOn }] });
      setTorchState(_ => ({ ..._, isOn }));
    } catch(e) {
      // 非対応端末では無視してよい
    }
  };

  return { videoRef, torch: { ...torchState, on: () => setTorch(true), off: () => setTorch(false) } };
};
